<template>
  <OmxCard :title="$t('comments-view.title')" icon="comment-text-outline" class="comments">
    <ul v-if="commentsData.length" class="comments__list">
      <li v-for="comment in commentsData" :key="comment.document_id" class="comments__item">
        <div class="comments__row">
          <div class="comments__meta">
            <span class="comments__avatar">
              <img v-if="avatarUrls[comment.user_id]" :src="avatarUrls[comment.user_id]"
                   :alt="getFullName(comment.meta.user.name)" />
              <span v-else>{{ commentInitials(comment.meta.user.name) }}</span>
            </span>
            <div class="comments__meta-text">
              <p class="comments__author">{{ getFullName(comment.meta.user.name) }}</p>
              <p class="comments__date">{{ getFormattedDateTime(comment.insert_ts) }}</p>
            </div>
          </div>
          <div class="comments__body">
            <div class="comments__bubble">{{ comment.text }}</div>
            <div class="comments__actions">
              <b-button v-if="!comment.isInReplyMode" size="is-small" icon-left="reply"
                        @click="replyCommentClicked(comment)">
                {{ $t('comments-view.reply-button') }}
              </b-button>
              <b-button v-if="isCurrentUser(comment.user_id) || currentUserAdminAccessLevel()"
                        type="is-danger" size="is-small" icon-left="delete"
                        @click="deleteCommentClicked(comment.document_id)">
                {{ $t('comments-view.delete-comment-button') }}
              </b-button>
            </div>

            <div v-if="comment.isInReplyMode" class="comments__reply">
              <b-input v-model="comment.subCommentText" type="textarea" />
              <div class="comments__reply-actions">
                <b-button size="is-small" @click="replyCommentClicked(comment)">
                  {{ $t('comments-view.reply-comment-cancel-button') }}
                </b-button>
                <b-button type="is-primary" size="is-small" @click="createSubCommentClicked(comment)">
                  {{ $t('comments-view.reply-comment-button') }}
                </b-button>
              </div>
            </div>

            <ul v-if="comment.projectssubcomments && comment.projectssubcomments.length" class="comments__sublist">
              <li v-for="sub in comment.projectssubcomments" :key="sub.document_id" class="comments__subitem">
                <div class="comments__meta">
                  <span class="comments__avatar comments__avatar--sm">
                    <img v-if="avatarUrls[sub.user_id]" :src="avatarUrls[sub.user_id]"
                         :alt="getFullName(sub.user.name)" />
                    <span v-else>{{ commentInitials(sub.user.name) }}</span>
                  </span>
                  <div class="comments__meta-text">
                    <p class="comments__author">{{ getFullName(sub.user.name) }}</p>
                    <p class="comments__date">{{ getFormattedDateTime(sub.insert_ts) }}</p>
                  </div>
                </div>
                <div class="comments__body">
                  <div class="comments__bubble">{{ sub.text }}</div>
                </div>
                <b-button v-if="isCurrentUser(sub.user_id) || currentUserAdminAccessLevel()"
                          type="is-danger" size="is-small" icon-left="delete"
                          @click="deleteCommentClicked(comment.document_id, sub.document_id)" />
              </li>
            </ul>
          </div>
        </div>
      </li>
    </ul>

    <OmxEmpty
        v-else
        icon="comment-text-outline"
        :title="$t('comments-view.empty-title')"
        tone="brand"
    />

    <div class="comments__composer">
      <b-input v-model="commentText" type="textarea" :placeholder="$t('comments-view.composer-placeholder')" />
      <div class="comments__composer-actions">
        <b-button type="is-primary" :disabled="!commentText.trim()" icon-left="send"
                  @click="createCommentClicked">
          {{ $t('comments-view.create-comment') }}
        </b-button>
      </div>
    </div>
  </OmxCard>
</template>

<script>
import firebase from "firebase/app";
import OmxCard from "@/components/OmxCard.vue";
import OmxEmpty from "@/components/OmxEmpty.vue";

export default {
  name: "Comments",
  components: { OmxCard, OmxEmpty },
  props: {
    'document_id': {
      type: String,
    }
  },
  data() {
    return {
      commentsData: [],
      commentText: "",
      // user_id -> profile pic download URL (null = looked up, none exists)
      avatarUrls: {}
    }
  },
  methods: {
    readCommentsData: function () {
      this.$parent.isLoading = true
      this.commentsData = []
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getCommentsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "get_comments",
        project_id: this.document_id,
        fill_meta: true,
        order_by: "modify_ts",
        order_direction: "ascending"
      }
      getCommentsFunction(payload)
          .then((result) => {
            this.$parent.isLoading = false
            result.data["projectscomments"].forEach((comment) => {
              comment.isInReplyMode = false
              comment.subCommentText = ""
              this.commentsData.push(comment)
            })
            this.loadAvatars()
          })
          .catch((error) => {
            this.$parent.isLoading = false
            this.showError(this.$t('messages.save-error'))
            console.error("Error reading comments:", error?.code)
          });
    },
    createCommentClicked: function () {
      const text = (this.commentText || "").toString().trim()
      if (!text) {
        this.showError(this.$t('messages.empty-form-error'))
        return
      }
      this.$parent.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertCommentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "insert_comment",
        project_id: this.document_id,
        user_id: this.getLocalUserId(),
        text: text
      }
      insertCommentFunction(payload)
          .then(() => {
            this.$parent.isLoading = false
            this.commentText = ""
            this.readCommentsData()
          })
          .catch((error) => {
            this.$parent.isLoading = false
            this.showError(this.$t('messages.save-error'))
            console.error("Error inserting comment:", error?.code)
          });
    },
    createSubCommentClicked: function (comment) {
      const text = (comment.subCommentText || "").toString().trim()
      if (!text) {
        this.showError(this.$t('messages.empty-form-error'))
        return
      }
      this.$parent.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertCommentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "insert_comment",
        project_id: this.document_id,
        user_id: this.getLocalUserId(),
        comment_id: comment.document_id,
        text: text
      }
      insertCommentFunction(payload)
          .then(() => {
            this.$parent.isLoading = false
            this.commentText = ""
            comment.subCommentText = ""
            this.readCommentsData()
          })
          .catch((error) => {
            this.$parent.isLoading = false
            this.showError(this.$t('messages.save-error'))
            console.error("Error inserting reply:", error?.code)
          });
    },
    deleteComment: function (commentId, subCommentId) {
      this.$parent.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteCommentFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const deleteCommentPayload = {
        action: "delete_comment",
        project_id: this.document_id,
        document_id: commentId
      }
      if (subCommentId !== undefined && subCommentId !== "" && subCommentId !== null) {
        deleteCommentPayload.comment_id = commentId
        deleteCommentPayload.document_id = subCommentId
      }
      deleteCommentFunction(deleteCommentPayload).then(() => {
        this.$parent.isLoading = false;
        this.showSuccess(this.$t('comments-view.comment-deleted'));
        this.readCommentsData()
      }).catch((error) => {
        this.$parent.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error deleting comment:", error?.code)
      });
    },
    async deleteCommentClicked(commentId, subCommentId) {
      const ok = await this.$omxConfirm({
        title: this.$t('comments-view.delete-comment-title').toString(),
        message: this.$t('comments-view.delete-comment-message').toString(),
        confirmText: this.$t('comments-view.delete-comment-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteComment(commentId, subCommentId);
    },
    replyCommentClicked: function (comment) {
      comment.isInReplyMode = !comment.isInReplyMode;
    },
    loadAvatars: function () {
      const userIds = new Set()
      this.commentsData.forEach((comment) => {
        if (comment.user_id) userIds.add(comment.user_id)
        const subs = comment.projectssubcomments || []
        subs.forEach((sub) => { if (sub.user_id) userIds.add(sub.user_id) })
      })
      userIds.forEach((userId) => {
        if (userId in this.avatarUrls) return
        this.$set(this.avatarUrls, userId, null)
        const imagePath = this.getStorageRootFolder() + '/users/' + userId + '/profilePic'
        firebase.storage().ref(imagePath).getDownloadURL()
            .then((url) => { this.$set(this.avatarUrls, userId, url) })
            .catch(() => { /* no profile pic — initials fallback stays */ })
      })
    },
    commentInitials: function (name) {
      if (!name) return "·"
      const first = (name.first || "").charAt(0)
      const last = (name.last || "").charAt(0)
      return (first + last).toUpperCase() || "·"
    }
  },
  mounted() {
    this.readCommentsData();
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.comments__list,
.comments__sublist {
  list-style: none;
  margin: 0;
  padding: 0;
}

.comments__item + .comments__item {
  border-top: 1px solid $omx-ink-100;
  margin-top: 14px;
  padding-top: 14px;
}

.comments__row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 16px;
  align-items: flex-start;
}

.comments__meta {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.comments__meta-text {
  min-width: 0;
}

.comments__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  background: $omx-brand-100;
  color: $omx-brand-700;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &--sm {
    width: 24px;
    height: 24px;
    font-size: 9.5px;
  }
}
.comments__author {
  margin: 0;
  font-weight: 600;
  color: $omx-ink-900;
  font-size: 13.5px;
}
.comments__date {
  margin: 2px 0 0;
  color: $omx-ink-500;
  font-size: 11.5px;
}

.comments__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.comments__bubble {
  background: $omx-ink-50;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-md;
  padding: 10px 12px;
  font-size: 13.5px;
  color: $omx-ink-700;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.comments__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.comments__reply {
  background: $omx-ink-50;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-md;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.comments__reply-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.comments__sublist {
  margin-top: 6px;
  padding-left: 16px;
  border-left: 2px solid $omx-ink-100;
}
.comments__subitem {
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: 12px;
  align-items: flex-start;
  padding: 8px 0;
}
.comments__subitem + .comments__subitem {
  border-top: 1px dashed $omx-ink-100;
}

.comments__composer {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.comments__composer-actions {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 700px) {
  .comments__row,
  .comments__subitem {
    grid-template-columns: 1fr;
  }
  .comments__sublist {
    padding-left: 10px;
  }
}
</style>
